<template>
  <div
    class="ranking-header-container"
    ref="ranking"
    :style="{
      marginBottom: bottom + 'px',
    }"
    @mouseenter="showScrollButtons = true"
    @mouseleave="showScrollButtons = false"
  >
    <!-- 左侧滚动按钮 -->
    <div
      v-show="leftShow && showScrollButtons"
      @click="scrollBy(-scrollStep)"
      class="scroll-btn left-btn"
      @mouseenter="isLeftBtnHovered = true"
      @mouseleave="isLeftBtnHovered = false"
    >
      <svg-btn class="icon" icon-class="icon-gfans-tab-left"></svg-btn>
    </div>

    <!-- 排行榜标签列表 -->
    <div
      class="ranking-tabs"
      ref="tabsContainer"
      @mousedown="startDrag"
      @mousemove="handleDrag"
      @mouseup="endDrag"
      @mouseleave="endDrag"
      @touchstart="startDrag"
      @touchmove="handleDrag"
      @touchend="endDrag"
    >
      <div class="tab-list" ref="tabList" @scroll="updateScrollButtons">
        <div
          v-for="(tab, index) in tabs"
          :key="tab.id"
          class="tab-item"
          @click="handleTabClick(tab, index)"
        >
          <el-image
            :class="['headImg', { headAct: currentTab == index }]"
            :src="tab.logo"
            fit="cover"
          >
            <img
              width="56"
              height="56"
              slot="error"
              class="error-headImg"
              :src="$utils.getPng('icon-gfans-defaultHead')"
            />
          </el-image>
          <!-- 有更新才需要出现这个 -->
          <div v-if="tab.is_update == 1" class="upBox"></div>
          <span class="tab-text">{{ tab.nickname }}</span>
        </div>
      </div>
    </div>

    <!-- 右侧滚动按钮 -->
    <div
      v-show="rightShow && showScrollButtons"
      @click="scrollBy(scrollStep)"
      class="scroll-btn right-btn"
      @mouseenter="isRightBtnHovered = true"
      @mouseleave="isRightBtnHovered = false"
    >
      <svg-btn class="icon" icon-class="icon-gfans-tab-right"></svg-btn>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    //数据列表
    tabs: {
      type: Array,
      default: () => [],
    },
    currentTab: {
      type: [String, Number],
      default: -1,
    },
    //底部间距
    bottom: {
      type: [String, Number],
      default: 22,
    },
  },
  data() {
    return {
      showScrollButtons: false,
      isDragging: false,
      dragStartX: 0,
      initialScrollLeft: 0,
      isLeftBtnHovered: false,
      isRightBtnHovered: false,
      scrollStep: 600,
      dragThreshold: 5,
      leftShow: false,
      rightShow: false,
      resizeTimeout: null,
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollButtons()
      this.ensureTabVisible(this.currentTab)
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    //恢复滚动位置
    scrollToStart() {
      const tabList = this.$refs.tabList
      if (!tabList) return
      tabList.scrollTo({
        left: 0,
      })
    },
    //父组件调用该方法来消除更新状态
    checkUpdata(row) {
      const targetIndex = this.tabs.findIndex(item => item.fluid == row)
      if (targetIndex !== -1) {
        this.$set(this.tabs[targetIndex], 'is_update', 0)
      }
    },
    //父组件调用该方法来删除某个订阅号
    checkDelete(row) {
      const targetIndex = this.tabs.findIndex(item => item.uid == row)
      if (targetIndex !== -1) {
        this.tabs.splice(targetIndex, 1)
      }
    },
    startDrag(e) {
      if (
        (e.type === 'mousedown' && e.button !== 0) ||
        (e.type === 'touchstart' && e.touches.length > 1)
      ) {
        return
      }
      this.isDragging = true
      this.dragStartX =
        e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
      this.initialScrollLeft = this.$refs.tabList.scrollLeft
      e.preventDefault()
      this.$refs.tabsContainer.classList.add('dragging')
    },

    handleDrag(e) {
      if (!this.isDragging) return
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
      const deltaX = this.dragStartX - clientX

      if (Math.abs(deltaX) > this.dragThreshold) {
        this.$refs.tabList.scrollLeft = this.initialScrollLeft + deltaX
      }
    },

    endDrag() {
      if (!this.isDragging) return
      this.isDragging = false
      this.$refs.tabsContainer.classList.remove('dragging')
    },

    handleTabClick(tab, index) {
      if (
        this.isDragging ||
        Math.abs(this.$refs.tabList.scrollLeft - this.initialScrollLeft) >
          this.dragThreshold
      ) {
        return
      }
      this.switchTab(tab, index)
    },

    switchTab(tab, index) {
      if (this.currentTab == index) return
      this.$emit('checkTab', tab, index)
      this.ensureTabVisible(index)
    },

    ensureTabVisible(index) {
      const tabList = this.$refs.tabList
      const tab = tabList?.children[index]
      if (!tab || !tabList) return

      // 获取容器和标签的位置（基于组件内的坐标）
      const tabRect = tab.getBoundingClientRect() // 标签在视口中的位置
      const containerRect = tabList.getBoundingClientRect() // 滚动容器在视口中的位置
      const containerScrollLeft = tabList.scrollLeft // 容器已滚动的距离

      // 计算标签在容器内的偏移量（使其居中）
      const tabCenter = tabRect.left - containerRect.left + tab.offsetWidth / 2
      const containerCenter = containerRect.width / 2
      const targetScrollLeft =
        containerScrollLeft + (tabCenter - containerCenter)

      // 限制滚动范围（避免越界）
      const minScroll = 0
      const maxScroll = tabList.scrollWidth - tabList.clientWidth
      const finalScrollLeft = Math.max(
        minScroll,
        Math.min(targetScrollLeft, maxScroll)
      )

      // 平滑滚动
      tabList.scrollTo({
        left: finalScrollLeft,
        behavior: 'smooth',
      })
    },

    scrollBy(delta) {
      const tabList = this.$refs.tabList
      if (!tabList) return

      tabList.scrollBy({
        left: delta,
        behavior: 'smooth',
      })
    },

    canLeft() {
      const tabList = this.$refs.tabList
      this.leftShow = tabList?.scrollLeft > 0
    },

    canRight() {
      const tabList = this.$refs.tabList
      this.rightShow = tabList
        ? tabList.scrollLeft + tabList.clientWidth < tabList.scrollWidth - 20
        : false
    },

    // 更新滚动按钮状态
    updateScrollButtons() {
      // 获取当前容器宽度
      this.scrollStep = this.$refs.ranking.clientWidth
      this.canLeft()
      this.canRight()
    },

    handleResize() {
      // 防抖处理
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        this.updateScrollButtons()
        this.ensureTabVisible(this.currentTab)
      }, 100)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
